import { type CSSProperties, memo, type RefObject } from 'react';

import { ImageSearchIcon, ImageUpIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type DropZone } from '~/components/PrivateDropzone/types';
import { SearchCommandVisualSearchHeader } from '~/components/SearchCommand/SearchCommandVisualSearchHeader';
import { SearchCommandVisualSearchWell } from '~/components/SearchCommand/SearchCommandVisualSearchWell';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type ImageSearchZoneOverlayProps = {
  hoveredZone: DropZone | null;
  dropZoneSearchByImageRef: RefObject<HTMLDivElement>;
  showSearchByImageOverlay: boolean;
  searchCommandTriggerPosition: DOMRect | null;
  dropzoneDescription: string;
};

export const ImageSearchZoneOverlay = memo(
  ({
    hoveredZone,
    dropZoneSearchByImageRef,
    showSearchByImageOverlay,
    searchCommandTriggerPosition,
    dropzoneDescription,
  }: ImageSearchZoneOverlayProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();

    if (!searchCommandTriggerPosition) {
      return null;
    }

    const isDropTarget = hoveredZone === 'search-by-image';
    const overlayPositionStyle: CSSProperties = {
      left: searchCommandTriggerPosition.left,
      top: searchCommandTriggerPosition.top,
      width: searchCommandTriggerPosition.width,
    };

    return (
      <div
        ref={dropZoneSearchByImageRef}
        style={overlayPositionStyle}
        className={cn(
          'absolute z-1 flex flex-col transition-opacity',
          !ui2026 && 'gap-1',
          showSearchByImageOverlay ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        data-testid="SEARCH_BY_IMAGE_OVERLAY_ROOT"
      >
        {ui2026 ? (
          <div
            className="flex flex-col overflow-hidden rounded-20 border ads:border-strong border-gray-6 ads:bg-base bg-gray-1 shadow-search-command"
            data-testid="SEARCH_BY_IMAGE_OVERLAY"
          >
            <SearchCommandVisualSearchHeader />
            <div className="px-3 pb-3">
              <SearchCommandVisualSearchWell appearance="plain" isDragActive={isDropTarget}>
                <div className="flex flex-col items-center gap-2 text-center">
                  <ImageUpIcon className="ads:text-icon-emphasis text-gray-12" size="lg" />
                  <p className="font-medium ads:text-primary text-14 text-gray-12">
                    {isDropTarget ? 'Drop here to search' : 'Drag and drop image here to search'}
                  </p>
                  <p className="ads:text-metadata text-12 text-gray-10">{dropzoneDescription}</p>
                </div>
              </SearchCommandVisualSearchWell>
            </div>
          </div>
        ) : (
          <>
            <div
              className="rounded-8 bg-transparent shadow-search-command"
              data-testid="SEARCH_BY_IMAGE_TRIGGER_OVERLAY"
              style={{
                width: searchCommandTriggerPosition.width,
                height: searchCommandTriggerPosition.height,
              }}
            />
            <div
              data-testid="SEARCH_BY_IMAGE_OVERLAY"
              className="rounded-8 border ads:border-strong border-gray-6 ads:bg-base bg-gray-1 shadow-search-command"
            >
              <div className="flex flex-col gap-1 px-4 py-3">
                <div className="px-1 py-0.5 font-bold ads:text-metadata text-10 text-gray-9 uppercase tracking-[0.4px]">
                  Search by image
                </div>
                <div
                  className={cn(
                    'flex min-h-[184px] items-center justify-center rounded-8 border ads:border-strong border-gray-7 border-dashed px-4 py-8 transition-colors',
                    isDropTarget &&
                      'ads:border-primary border-blue-9 border-solid ads:bg-primary-subtle bg-blue-1 outline ads:outline-primary outline-1 outline-blue-9',
                  )}
                >
                  <div className="flex flex-col items-center gap-2 text-center">
                    <ImageSearchIcon className="ads:text-icon-emphasis text-gray-12" size="lg" />
                    <p className="font-medium ads:text-primary text-14 text-gray-12">
                      {isDropTarget ? 'Drop here to search' : 'Drag and drop image here to search'}
                    </p>
                    <p className="ads:text-metadata text-12 text-gray-10">{dropzoneDescription}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  },
);

ImageSearchZoneOverlay.displayName = 'ImageSearchZoneOverlay';
